import { useQuery } from '@tanstack/react-query';
import type { DashboardPeriod, SchemaField } from '../types';
import type { EndpointMeta } from '../api/dashboardProxy';
import type { CachedWidgetEnvelope } from '../api/widgetEnvelope';
import { fetchWidgetEnvelope, widgetDataQueryKey } from '../api/widgetEnvelope';
import { useDashboardContext } from '../useDashboardContext';

interface UseWidgetFetchResult<T> {
  data: T | null;
  schema: { fields: SchemaField[] } | null;
  meta: EndpointMeta | null;
  loading: boolean;
  error: string | null;
}

/**
 * Hook centralizado para fetching de datos de widgets.
 * Retorna data (post field_mapping) + schema del endpoint (si disponible).
 *
 * <b>La clave y el contenido de la entrada de caché los define `api/widgetEnvelope`</b>, no este
 * hook: `useSecondarySource` comparte esa misma entrada a propósito y las dos tienen que guardar
 * exactamente lo mismo. Cuando el `field_mapping` vivía aquí dentro, el que perdía la carrera no
 * ejecutaba su `queryFn` y leía datos sin renombrar.
 */
export function useWidgetFetch<T>(
  widgetId: string,
  period: DashboardPeriod,
  refreshPolicy?: number,
  fieldMapping?: Record<string, string>
): UseWidgetFetchResult<T> {
  const { fetcher } = useDashboardContext();

  const { data: result, isLoading, error } = useQuery<CachedWidgetEnvelope<T>>({
    queryKey: widgetDataQueryKey(widgetId, period),
    queryFn: () => fetchWidgetEnvelope<T>({ widgetId, period }, fieldMapping, fetcher),
    // Mínimo 10 segundos para evitar polling agresivo por configuración incorrecta
    refetchInterval: refreshPolicy && refreshPolicy > 0
      ? Math.max(refreshPolicy, 10) * 1000
      : false,
  });

  return {
    data: result?.data ?? null,
    schema: result?.schema ?? null,
    meta: result?.meta ?? null,
    loading: isLoading,
    error: error ? (error as Error).message : null,
  };
}
